"use client"

import { DropPreviewType } from "@anaconda/core/drop/types"
import type { Media } from "@anaconda/core/nft/types"
import { useLingui } from "@lingui/react/macro"
import { Skeleton } from "@rarible_int/anaconda_ui_components"
import { reatomComponent } from "@reatom/react"
import { Suspense, memo } from "react"
import { ErrorBoundary } from "react-error-boundary"
import { ItemMedia, PLACEHOLDER_IMAGE_SRC } from "../../item/view/item-media"
import { detectMediaTypeFromUrl } from "../../utils/media-type"
import {
  getBestMediaUrl,
  getVideoPosterUrl,
} from "../../utils/rarible-media-url"
import { useNftReatom } from "../store/hooks.reatom"

export function NftImageMediaReatom({
  useCloudinaryUrls = false,
}: {
  useCloudinaryUrls?: boolean
}) {
  const { t } = useLingui()

  return (
    <ErrorBoundary
      fallback={
        <ItemMedia
          src={PLACEHOLDER_IMAGE_SRC}
          alt={t`failed to load nft`}
          className="object-contain"
        />
      }
    >
      <Suspense fallback={<Skeleton className="h-full w-full" />}>
        <NftImageMediaViewReatom useCloudinaryUrls={useCloudinaryUrls} />
      </Suspense>
    </ErrorBoundary>
  )
}

const NftImageMediaViewReatom = reatomComponent(
  ({ useCloudinaryUrls = false }: { useCloudinaryUrls?: boolean }) => {
    const nft = useNftReatom()

    if (useCloudinaryUrls) {
      return <NftImageMediaContent imageSrc={nft.image} nftName={nft.name} />
    }

    return (
      <NftMediaContent
        media={nft.media}
        imageSrc={nft.image}
        nftName={nft.name}
      />
    )
  },
  "NftImageMediaViewReatom",
)

const NftMediaContent = memo(function NftImageMediaContent({
  media,
  imageSrc,
  nftName,
}: {
  media?: Media
  imageSrc?: string
  nftName: string
}) {
  const { t } = useLingui()

  return (
    <ItemMedia
      src={media?.url}
      alt={t`Media for ${nftName} item`}
      type={media?.type}
      className="object-contain"
      posterUrl={imageSrc}
      videoProps={{
        autoPlay: true,
        loop: true,
        muted: true,
      }}
    />
  )
})

const NftImageMediaContent = memo(function NftImageMediaContent({
  imageSrc,
  nftName,
}: {
  imageSrc?: string
  nftName: string
}) {
  const { t } = useLingui()

  const mediaType = detectMediaTypeFromUrl(imageSrc)
  // Get the best media URL (original if available, thumbnail as fallback)
  const mediaUrl =
    mediaType !== DropPreviewType.Image ? getBestMediaUrl(imageSrc) : imageSrc

  // Get video poster URL if it's a video
  const posterUrl =
    mediaType === DropPreviewType.Video
      ? getVideoPosterUrl(imageSrc, mediaUrl)
      : undefined

  return (
    <ItemMedia
      src={mediaUrl}
      alt={t`Media for ${nftName} item`}
      type={mediaType}
      className="object-contain"
      posterUrl={posterUrl}
      videoProps={{
        autoPlay: true,
        loop: true,
        muted: true,
      }}
    />
  )
})
